/**
 * Calendar of Harptos - Forgotten Realms Date Library
 * https://forgottenrealms.fandom.com/wiki/Calendar_of_Harptos
 */

const MONTHS = [
  'Hammer', 'Alturiak', 'Ches', 'Tarsakh', 'Mirtul', 'Kythorn',
  'Flamerule', 'Eleasis', 'Eleint', 'Marpenoth', 'Uktar', 'Nightal'
];

const SPECIAL_DAYS = [
  { name: 'Midwinter', afterMonth: 0 },
  { name: 'Greengrass', afterMonth: 3 },
  { name: 'Midsummer', afterMonth: 6 },
  { name: 'Highharvesttide', afterMonth: 8 },
  { name: 'Feast of the Moon', afterMonth: 10 }
];

const NOTABLE_DATES = {
  'Ches 19': 'Spring Equinox',
  'Kythorn 20': 'Summer Solstice',
  'Eleint 21': 'Autumn Equinox',
  'Nightal 20': 'Winter Solstice'
};

export function isLeapYear(year) {
  return year % 4 === 0;
}

export function daysInYear(year) {
  return isLeapYear(year) ? 366 : 365;
}

export function isSpecialDay(name) {
  return SPECIAL_DAYS.some(s => s.name === name) || name === 'Shieldmeet';
}

export function isMonth(name) {
  return MONTHS.includes(name);
}

/**
 * Convert a Harptos date to day-of-year (1-indexed)
 */
export function toDayOfYear(month, day, year) {
  if (isSpecialDay(month)) {
    let doy = 0;
    for (let i = 0; i < 12; i++) {
      const special = SPECIAL_DAYS.find(s => s.afterMonth === i);
      doy += 30;
      if (special) {
        if (special.name === month) return doy + 1;
        doy++;
        if (special.name === 'Midsummer' && month === 'Shieldmeet' && isLeapYear(year)) {
          return doy + 1;
        }
        if (special.name === 'Midsummer' && isLeapYear(year)) doy++;
      }
    }
  }

  const monthIndex = MONTHS.indexOf(month);
  let doy = monthIndex * 30 + day;

  for (const special of SPECIAL_DAYS) {
    if (special.afterMonth < monthIndex) {
      doy++;
      if (special.name === 'Midsummer' && isLeapYear(year)) doy++;
    }
  }

  return doy;
}

/**
 * Convert day-of-year back to Harptos date
 */
export function fromDayOfYear(doy, year) {
  let remaining = doy;

  for (let monthIndex = 0; monthIndex < 12; monthIndex++) {
    if (remaining <= 30) {
      return { month: MONTHS[monthIndex], day: remaining, year };
    }
    remaining -= 30;

    const special = SPECIAL_DAYS.find(s => s.afterMonth === monthIndex);
    if (special) {
      if (remaining === 1) {
        return { month: special.name, day: null, year, isSpecial: true };
      }
      remaining--;

      if (special.name === 'Midsummer' && isLeapYear(year)) {
        if (remaining === 1) {
          return { month: 'Shieldmeet', day: null, year, isSpecial: true };
        }
        remaining--;
      }
    }
  }

  return { month: MONTHS[11], day: 30, year };
}

/**
 * Convert a Harptos date to an absolute day number (for timeline positioning)
 * Uses year 0 as epoch
 */
export function toAbsoluteDay(month, day, year) {
  let totalDays = 0;

  // Add days for complete years
  for (let y = 0; y < year; y++) {
    totalDays += daysInYear(y);
  }

  // Add days within the current year
  totalDays += toDayOfYear(month, day || 1, year);

  return totalDays;
}

/**
 * Convert absolute day back to Harptos date
 */
export function fromAbsoluteDay(absoluteDay) {
  let remaining = absoluteDay;
  let year = 0;

  while (remaining > daysInYear(year)) {
    remaining -= daysInYear(year);
    year++;
  }

  return fromDayOfYear(remaining, year);
}

/**
 * Format a Harptos date for display
 */
export function formatDate(dateObj) {
  if (!dateObj) return '';
  const { month, day, year, isSpecial } = dateObj;

  if (isSpecial || !day) {
    return `${month}, ${year} DR`;
  }

  const dateStr = `${month} ${day}, ${year} DR`;
  const notable = NOTABLE_DATES[`${month} ${day}`];

  return notable ? `${dateStr} (${notable})` : dateStr;
}

/**
 * Parse a date object from JSON format
 */
export function parseDate(obj) {
  if (!obj) return null;
  return {
    month: obj.month,
    day: obj.day || null,
    year: obj.year,
    isSpecial: isSpecialDay(obj.month)
  };
}

/**
 * Get all months and special days for dropdowns
 */
export function getDateOptions() {
  const options = [];

  for (let i = 0; i < 12; i++) {
    options.push({ value: MONTHS[i], label: MONTHS[i], isSpecial: false });

    const special = SPECIAL_DAYS.find(s => s.afterMonth === i);
    if (special) {
      options.push({ value: special.name, label: `${special.name}`, isSpecial: true });
      if (special.name === 'Midsummer') {
        options.push({ value: 'Shieldmeet', label: 'Shieldmeet (leap years)', isSpecial: true });
      }
    }
  }

  return options;
}

/**
 * Convert Harptos date to JavaScript Date for timeline positioning
 * Maps Harptos years to JS years (1492 DR -> 1492 CE)
 */
export function toJSDate(month, day, year) {
  const doy = toDayOfYear(month, day || 1, year);
  // Use January 1 of the year and add days
  const date = new Date(year, 0, 1);
  date.setDate(doy);
  return date;
}

/**
 * Convert JavaScript Date back to Harptos date
 */
export function fromJSDate(jsDate) {
  const year = jsDate.getFullYear();
  const startOfYear = new Date(year, 0, 1);
  const diff = jsDate - startOfYear;
  const doy = Math.floor(diff / (1000 * 60 * 60 * 24)) + 1;
  return fromDayOfYear(doy, year);
}

/**
 * Add days to a Harptos date
 */
export function addDays(month, day, year, daysToAdd) {
  let doy = toDayOfYear(month, day || 1, year);
  doy += daysToAdd;

  while (doy > daysInYear(year)) {
    doy -= daysInYear(year);
    year++;
  }

  while (doy < 1) {
    year--;
    doy += daysInYear(year);
  }

  return fromDayOfYear(doy, year);
}

/**
 * Calculate the number of days between two Harptos dates
 */
export function daysBetween(month1, day1, year1, month2, day2, year2) {
  let totalDays = 0;

  if (year1 === year2) {
    return toDayOfYear(month2, day2 || 1, year2) - toDayOfYear(month1, day1 || 1, year1);
  }

  const sign = year2 > year1 ? 1 : -1;

  if (sign === 1) {
    totalDays += daysInYear(year1) - toDayOfYear(month1, day1 || 1, year1);
    for (let y = year1 + 1; y < year2; y++) {
      totalDays += daysInYear(y);
    }
    totalDays += toDayOfYear(month2, day2 || 1, year2);
  } else {
    totalDays -= daysInYear(year2) - toDayOfYear(month2, day2 || 1, year2);
    for (let y = year2 + 1; y < year1; y++) {
      totalDays -= daysInYear(y);
    }
    totalDays -= toDayOfYear(month1, day1 || 1, year1);
  }

  return totalDays;
}

/**
 * Get notable event for a date (solstice, equinox, etc.)
 */
export function getNotableEvent(month, day) {
  return NOTABLE_DATES[`${month} ${day}`] || null;
}

export { MONTHS, SPECIAL_DAYS, NOTABLE_DATES };
